import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gift, Search, Clock, Copy, MapPin, Check, Eye,
  ChevronRight, Wallet, RefreshCw, Lock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api, errorMessage } from '../lib/api';

// ============================================
// V5 DESIGN CONSTANTS
// ============================================
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    orangeDark: '#ea580c',
    teal: '#14b8a6',
    green: '#22c55e',
    amber: '#f59e0b',
    red: '#ef4444',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
    borderDefault: 'rgba(30, 27, 75, 0.1)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

const MAX_WALLET_SLOTS = 4;
const DEAL_EXPIRATION_DAYS = 7;

// ============================================
// DYNAMIC BACKGROUND - Option 1: Floating Gradient Blobs
// ============================================
const FloatingGradientBlobs = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    {/* Large purple blob - top right */}
    <motion.div
      animate={{
        x: [0, 30, -20, 0],
        y: [0, -40, 20, 0],
        scale: [1, 1.1, 0.95, 1],
      }}
      transition={{
        duration: 20,
        repeat: Infinity,
        ease: "easeInOut",
      }}
      className="absolute -top-32 -right-32 w-96 h-96 rounded-full"
      style={{
        background: `radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 50%, transparent 70%)`,
        filter: 'blur(40px)',
      }}
    />

    {/* Orange blob - bottom left */}
    <motion.div
      animate={{
        x: [0, -25, 35, 0],
        y: [0, 30, -25, 0],
        scale: [1, 0.9, 1.05, 1],
      }}
      transition={{
        duration: 25,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 2,
      }}
      className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full"
      style={{
        background: `radial-gradient(circle, rgba(249, 115, 22, 0.12) 0%, rgba(249, 115, 22, 0.04) 50%, transparent 70%)`,
        filter: 'blur(40px)',
      }}
    />

    {/* Small amber blob - center right */}
    <motion.div
      animate={{
        x: [0, 40, -30, 0],
        y: [0, -20, 30, 0],
      }}
      transition={{
        duration: 18,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 5,
      }}
      className="absolute top-1/3 right-1/4 w-48 h-48 rounded-full"
      style={{
        background: `radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 70%)`,
        filter: 'blur(30px)',
      }}
    />

    {/* Small purple blob - bottom center */}
    <motion.div
      animate={{
        x: [0, -20, 25, 0],
        y: [0, 35, -15, 0],
      }}
      transition={{
        duration: 22,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 8,
      }}
      className="absolute bottom-1/4 left-1/3 w-56 h-56 rounded-full"
      style={{
        background: `radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%)`,
        filter: 'blur(35px)',
      }}
    />
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const MyCodes = () => {
  const { isAuthenticated, user, loading: authLoading } = useAuth();

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revealedCodes, setRevealedCodes] = useState({});
  const [copiedCode, setCopiedCode] = useState(null);

  const isExpired = (claim) => {
    const expiresDate = claim.expires_at
      ? new Date(claim.expires_at)
      : new Date(new Date(claim.claimed_at).getTime() + DEAL_EXPIRATION_DAYS * 24 * 60 * 60 * 1000);
    return expiresDate < new Date();
  };

  const activeClaims = claims.filter(c => !c.redeemed && !isExpired(c));
  const redeemedClaims = claims.filter(c => c.redeemed);
  const expiredClaims = claims.filter(c => !c.redeemed && isExpired(c));

  const activeCount = activeClaims.length;
  const redeemedCount = redeemedClaims.length;
  const emptySlots = Math.max(0, MAX_WALLET_SLOTS - activeCount);

  useEffect(() => {
    if (isAuthenticated) {
      fetchClaims();
    }
  }, [isAuthenticated]);

  const fetchClaims = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data } = await api.get('/deals/my-claims');
      setClaims(Array.isArray(data) ? data : []);

    } catch (err) {
      setError(errorMessage(err, 'We could not load your deals. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const getExpirationText = (claim) => {
    const expiresDate = claim.expires_at
      ? new Date(claim.expires_at)
      : new Date(new Date(claim.claimed_at).getTime() + DEAL_EXPIRATION_DAYS * 24 * 60 * 60 * 1000);

    const now = new Date();
    const diffMs = expiresDate - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

    if (diffDays < 0) return { text: 'Expired', color: 'red' };
    if (diffHours <= 24 && diffHours > 0) return { text: `${diffHours}h left`, color: 'red' };
    if (diffDays === 0) return { text: 'Expires today', color: 'red' };
    if (diffDays === 1) return { text: '1 day left', color: 'amber' };
    if (diffDays <= 3) return { text: `${diffDays} days left`, color: 'amber' };
    return { text: `${diffDays} days left`, color: 'green' };
  };

  const handleRevealCode = (claimId) => {
    setRevealedCodes(prev => ({ ...prev, [claimId]: true }));
  };

  const handleCopyCode = async (code, claimId) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(code);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedCode(claimId);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getBusinessEmoji = (businessName) => {
    const name = businessName?.toLowerCase() || '';
    if (name.includes('yoga') || name.includes('wellness') || name.includes('spa')) return '🧘';
    if (name.includes('auto') || name.includes('car') || name.includes('tire')) return '🚗';
    if (name.includes('pizza')) return '🍕';
    if (name.includes('restaurant') || name.includes('table') || name.includes('grill') || name.includes('kitchen')) return '🍽️';
    if (name.includes('coffee') || name.includes('cafe') || name.includes('bakery')) return '☕';
    if (name.includes('ups') || name.includes('shipping') || name.includes('mail')) return '📦';
    if (name.includes('book') || name.includes('library')) return '📚';
    if (name.includes('bar') || name.includes('pub') || name.includes('tavern')) return '🍺';
    if (name.includes('gym') || name.includes('fitness')) return '💪';
    if (name.includes('salon') || name.includes('barber') || name.includes('hair')) return '💇';
    if (name.includes('flower') || name.includes('florist') || name.includes('garden')) return '🌸';
    if (name.includes('pet') || name.includes('vet') || name.includes('animal')) return '🐾';
    if (name.includes('hardware') || name.includes('tool')) return '🔧';
    if (name.includes('kid') || name.includes('child') || name.includes('learning')) return '🎨';
    if (name.includes('sushi') || name.includes('japanese')) return '🍣';
    if (name.includes('italian') || name.includes('cucina')) return '🇮🇹';
    if (name.includes('mexican') || name.includes('taco')) return '🌮';
    if (name.includes('chinese') || name.includes('asian')) return '🥡';
    return '🏪';
  };

  // AUTH CHECK
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: v5.colors.cream }}>
        <div
          className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: v5.colors.purple, borderTopColor: 'transparent' }}
        ></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: '/deal-wallet' }} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: v5.colors.cream }}>
        <div
          className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: v5.colors.purple, borderTopColor: 'transparent' }}
        ></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: v5.colors.cream }}>

      {/* V5 HERO SECTION */}
      <HeroSection
        activeCount={activeCount}
        redeemedCount={redeemedCount}
        maxSlots={MAX_WALLET_SLOTS}
      />

      {/* MAIN CONTENT */}
      <main className="max-w-6xl mx-auto px-6 py-12">

        {/* DEAL WALLET SLOTS */}
        <WalletSlotsSection
          activeClaims={activeClaims}
          emptySlots={emptySlots}
          revealedCodes={revealedCodes}
          copiedCode={copiedCode}
          onReveal={handleRevealCode}
          onCopy={handleCopyCode}
          getExpirationText={getExpirationText}
          getBusinessEmoji={getBusinessEmoji}
          formatDate={formatDate}
        />

        {/* Re-Claim Expired Deals Section */}
        {expiredClaims.length > 0 && (
          <ExpiredClaimsSection
            claims={expiredClaims}
            getBusinessEmoji={getBusinessEmoji}
            formatDate={formatDate}
          />
        )}

        {/* Redeemed Offers Section */}
        {redeemedClaims.length > 0 && (
          <RedeemedClaimsSection
            claims={redeemedClaims}
            getBusinessEmoji={getBusinessEmoji}
            formatDate={formatDate}
          />
        )}

      </main>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

// ============================================
// SUB-COMPONENTS
// ============================================

const HeroSection = ({ activeCount, redeemedCount, maxSlots }) => {
  // Code snippets for the grid background
  const codeSnippets = [
    '{ deals: 4 }',
    'claim()',
    '// wallet',
    'redeem()',
    '=> save',
    'slots++',
  ];

  return (
    <section className="relative overflow-hidden" style={{ paddingTop: '8rem' }}>
      {/* Navy background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundColor: v5.colors.navy,
        boxShadow: '0 4px 20px rgba(30, 27, 75, 0.3)'
      }}></div>

      {/* Code Grid Pattern - Inside Hero */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Subtle grid pattern - 10% more visible */}
        <div
          className="absolute inset-0 opacity-[0.09]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />

        {/* Floating code snippets */}
        {codeSnippets.map((snippet, index) => (
          <motion.div
            key={index}
            animate={{
              opacity: [0.1, 0.25, 0.1],
              y: [0, -15, 0],
            }}
            transition={{
              duration: 6 + index * 1.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 1,
            }}
            className="absolute text-xs whitespace-nowrap"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              color: index % 2 === 0 ? 'rgba(139, 92, 246, 0.5)' : 'rgba(249, 115, 22, 0.5)',
              top: `${15 + (index * 15) % 70}%`,
              left: `${5 + (index * 18) % 85}%`,
              fontSize: '11px',
            }}
          >
            {snippet}
          </motion.div>
        ))}

        {/* Gradient accents */}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 20% 30%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
                         radial-gradient(ellipse at 80% 70%, rgba(249, 115, 22, 0.12) 0%, transparent 50%)`,
          }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 md:py-20 text-center">
      {/* Code accent */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
        style={{
          fontFamily: v5.fonts.mono,
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.4)'
        }}
      >
        // deal_wallet
      </motion.p>

      {/* Wallet Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
      >
        <Wallet className="w-5 h-5" style={{ color: v5.colors.orange }} />
        <span
          className="font-medium"
          style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: v5.fonts.body }}
        >
          {activeCount}/{maxSlots} Slots Used
        </span>
      </motion.div>

      {/* Title */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-4xl md:text-5xl lg:text-6xl mb-4"
        style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.white }}
      >
        Deal Wallet
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-lg max-w-md mx-auto mb-8"
        style={{ fontFamily: v5.fonts.body, color: 'rgba(255, 255, 255, 0.6)' }}
      >
        Hold up to {maxSlots} active deals • Deals expire in 7 days
      </motion.p>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex items-center justify-center gap-6 md:gap-10"
      >
        <div className="text-center">
          <div
            className="text-3xl md:text-4xl font-bold"
            style={{ color: v5.colors.orange, fontFamily: v5.fonts.body }}
          >
            {activeCount}
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', fontFamily: v5.fonts.body }}>Active</div>
        </div>
        <div className="w-px h-12" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}></div>
        <div className="text-center">
          <div
            className="text-3xl md:text-4xl font-bold"
            style={{ color: v5.colors.green, fontFamily: v5.fonts.body }}
          >
            {redeemedCount}
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', fontFamily: v5.fonts.body }}>Redeemed</div>
        </div>
        <div className="w-px h-12" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}></div>
        <div className="text-center">
          <div
            className="text-3xl md:text-4xl font-bold"
            style={{ color: 'rgba(255, 255, 255, 0.7)', fontFamily: v5.fonts.body }}
          >
            {maxSlots - activeCount}
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', fontFamily: v5.fonts.body }}>Available</div>
        </div>
      </motion.div>
    </div>

    {/* V5 Purple Accent Line */}
    <div
      className="absolute bottom-0 left-0 w-full h-1"
      style={{ background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)` }}
    />
  </section>
  );
};

const WalletSlotsSection = ({
  activeClaims, emptySlots, revealedCodes, copiedCode,
  onReveal, onCopy, getExpirationText, getBusinessEmoji, formatDate
}) => (
  <section className="mb-16">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)' }}
        >
          <Wallet className="w-6 h-6" style={{ color: v5.colors.orange }} />
        </div>
        <div>
          <p
            style={{
              fontFamily: v5.fonts.mono,
              fontSize: '12px',
              color: v5.colors.textHint
            }}
          >
            // active_deals
          </p>
          <h2
            className="text-2xl"
            style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
          >
            Your Active Deals
          </h2>
        </div>
      </div>
      <span
        className="px-3 py-1 rounded-full text-sm font-semibold"
        style={{
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          color: v5.colors.orange,
          fontFamily: v5.fonts.body
        }}
      >
        {activeClaims.length}/{MAX_WALLET_SLOTS} Slots
      </span>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <AnimatePresence>
        {activeClaims.map((claim, index) => (
          <motion.div
            key={claim.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: index * 0.1 }}
          >
            <ActiveClaimCard
              claim={claim}
              slotNumber={index + 1}
              isRevealed={revealedCodes[claim.id]}
              isCopied={copiedCode === claim.id}
              onReveal={() => onReveal(claim.id)}
              onCopy={() => onCopy(claim.code, claim.id)}
              expirationInfo={getExpirationText(claim)}
              emoji={getBusinessEmoji(claim.business_name)}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Empty Slot Cards */}
      {Array.from({ length: emptySlots }).map((_, index) => (
        <motion.div
          key={`empty-${index}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: (activeClaims.length + index) * 0.1 }}
        >
          <EmptySlotCard slotNumber={activeClaims.length + index + 1} />
        </motion.div>
      ))}
    </div>
  </section>
);

const EmptySlotCard = ({ slotNumber }) => (
  <div
    className="rounded-xl overflow-hidden h-full min-h-[280px] flex flex-col"
    style={{
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      border: `2px dashed ${v5.colors.borderDefault}`
    }}
  >
    {/* Header */}
    <div
      className="px-6 py-4"
      style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)' }}
          >
            <Lock className="w-6 h-6" style={{ color: v5.colors.textHint }} />
          </div>
          <div>
            <span
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: v5.colors.textMuted, fontFamily: v5.fonts.body }}
            >
              Slot {slotNumber}
            </span>
            <h3
              className="font-bold"
              style={{ color: v5.colors.textSecondary, fontFamily: v5.fonts.body }}
            >
              Empty Slot
            </h3>
          </div>
        </div>
        <div
          className="px-2 py-1 rounded-full"
          style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)' }}
        >
          <span
            className="text-xs font-medium"
            style={{ color: v5.colors.textMuted, fontFamily: v5.fonts.body }}
          >
            Available
          </span>
        </div>
      </div>
    </div>

    {/* Content */}
    <div className="p-6 flex-1 flex flex-col items-center justify-center text-center">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
        style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)' }}
      >
        <Gift className="w-8 h-8" style={{ color: v5.colors.textHint }} />
      </div>
      <h4
        className="text-lg mb-2"
        style={{ fontFamily: v5.fonts.display, color: v5.colors.textMuted }}
      >
        Ready for a Deal
      </h4>
      <p
        className="text-sm mb-4"
        style={{ fontFamily: v5.fonts.body, color: v5.colors.textHint }}
      >
        This slot is waiting for your next deal!
      </p>
      <Link
        to="/local-deals-near-me"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all hover:-translate-y-0.5"
        style={{
          background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
          fontFamily: v5.fonts.body,
          boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
        }}
      >
        <Search className="w-4 h-4" />
        Find Deals
      </Link>
    </div>
  </div>
);

const ActiveClaimCard = ({
  claim, slotNumber, isRevealed, isCopied, onReveal, onCopy,
  expirationInfo, emoji
}) => {
  const getExpirationBadgeStyle = () => {
    if (expirationInfo?.color === 'red') {
      return { backgroundColor: 'rgba(255, 255, 255, 0.2)', color: '#FFFFFF' };
    }
    if (expirationInfo?.color === 'amber') {
      return { backgroundColor: 'rgba(255, 255, 255, 0.2)', color: '#FFFFFF' };
    }
    return { backgroundColor: 'rgba(255, 255, 255, 0.2)', color: '#FFFFFF' };
  };

  return (
    <div
      className="rounded-xl overflow-hidden h-full transition-all hover:-translate-y-1"
      style={{
        backgroundColor: v5.colors.white,
        border: `1px solid ${v5.colors.borderLight}`,
        boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
      }}
    >
      {/* Header */}
      <div
        className="px-6 py-4"
        style={{ background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.orangeDark} 100%)` }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm"
              style={{ backgroundColor: v5.colors.white }}
            >
              <span className="text-2xl">{emoji}</span>
            </div>
            <div>
              <span
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: 'rgba(255, 255, 255, 0.7)', fontFamily: v5.fonts.body }}
              >
                Slot {slotNumber}
              </span>
              <h3
                className="font-bold text-white"
                style={{ fontFamily: v5.fonts.body }}
              >
                {claim.business_name}
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
              style={getExpirationBadgeStyle()}
            >
              <Clock className="w-3 h-3" />
              {expirationInfo?.text}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="mb-4">
          <h4
            className="text-xl"
            style={{ fontFamily: v5.fonts.display, color: v5.colors.textPrimary }}
          >
            {claim.deal_title}
          </h4>
          {claim.deal_description && (
            <p
              className="text-sm mt-1 line-clamp-2"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
            >
              {claim.deal_description}
            </p>
          )}
        </div>

        {/* Code Display */}
        {!isRevealed ? (
          <>
            <div
              className="rounded-xl p-4 text-center mb-4"
              style={{
                border: `2px dashed rgba(249, 115, 22, 0.3)`,
                backgroundColor: 'rgba(249, 115, 22, 0.05)'
              }}
            >
              <p
                className="text-sm mb-2 font-semibold tracking-wide"
                style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
              >
                YOUR PROMO CODE
              </p>
              <div className="flex items-center justify-center gap-2 py-2">
                {[...Array(6)].map((_, i) => (
                  <span
                    key={i}
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: v5.colors.orange }}
                  ></span>
                ))}
              </div>
            </div>
            <button
              onClick={onReveal}
              className="w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
              style={{
                border: `2px solid ${v5.colors.orange}`,
                color: v5.colors.orange,
                fontFamily: v5.fonts.body
              }}
            >
              <Eye className="w-5 h-5" />
              Show Code
            </button>
          </>
        ) : (
          <>
            <div
              className="rounded-xl p-4 text-center mb-4"
              style={{
                border: `2px dashed rgba(249, 115, 22, 0.3)`,
                backgroundColor: 'rgba(249, 115, 22, 0.05)'
              }}
            >
              <p
                className="text-sm mb-2 font-semibold tracking-wide"
                style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
              >
                YOUR PROMO CODE
              </p>
              <p
                className="text-3xl font-bold tracking-widest"
                style={{ fontFamily: v5.fonts.mono, color: v5.colors.textPrimary }}
              >
                {claim.code}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={onCopy}
                className="py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all"
                style={{
                  backgroundColor: isCopied ? v5.colors.green : v5.colors.navy,
                  color: v5.colors.white,
                  fontFamily: v5.fonts.body
                }}
              >
                {isCopied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy Code
                  </>
                )}
              </button>
              <Link
                to={`/business/${claim.business_slug}`}
                className="py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                style={{
                  border: `2px solid ${v5.colors.borderDefault}`,
                  color: v5.colors.textPrimary,
                  fontFamily: v5.fonts.body
                }}
              >
                <MapPin className="w-4 h-4" />
                Directions
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ExpiredClaimsSection = ({ claims, getBusinessEmoji, formatDate }) => (
  <section className="mb-16">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}
        >
          <RefreshCw className="w-6 h-6" style={{ color: v5.colors.amber }} />
        </div>
        <div>
          <p
            style={{
              fontFamily: v5.fonts.mono,
              fontSize: '12px',
              color: v5.colors.textHint
            }}
          >
            // expired_deals
          </p>
          <h2
            className="text-2xl"
            style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
          >
            Re-Claim Expired Deals
          </h2>
        </div>
      </div>
      <span
        className="px-3 py-1 rounded-full text-sm font-semibold"
        style={{
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          color: v5.colors.amber,
          fontFamily: v5.fonts.body
        }}
      >
        {claims.length} Available
      </span>
    </div>

    <div className="grid md:grid-cols-3 gap-4">
      <AnimatePresence>
        {claims.map((claim, index) => (
          <motion.div
            key={claim.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: index * 0.05 }}
          >
            <ExpiredClaimCard
              claim={claim}
              emoji={getBusinessEmoji(claim.business_name)}
              formatDate={formatDate}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  </section>
);

const ExpiredClaimCard = ({ claim, emoji, formatDate }) => (
  <div
    className="rounded-xl p-5 transition-all hover:-translate-y-1"
    style={{
      backgroundColor: v5.colors.white,
      border: `1px solid ${v5.colors.borderLight}`,
      boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
      borderLeft: `4px solid ${v5.colors.amber}`
    }}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
          style={{ backgroundColor: v5.colors.white, border: `1px solid ${v5.colors.borderLight}` }}
        >
          <span className="text-xl">{emoji}</span>
        </div>
        <div>
          <h4
            className="font-semibold"
            style={{ fontFamily: v5.fonts.body, color: v5.colors.textPrimary }}
          >
            {claim.business_name}
          </h4>
          <p
            className="text-xs"
            style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
          >
            Expired {formatDate(claim.expires_at || claim.claimed_at)}
          </p>
        </div>
      </div>
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}
      >
        <Clock className="w-4 h-4" style={{ color: v5.colors.amber }} />
      </div>
    </div>

    <div className="mb-3">
      <p
        className="text-sm line-clamp-2"
        style={{ fontFamily: v5.fonts.body, color: v5.colors.textSecondary }}
      >
        {claim.deal_title}
      </p>
    </div>

    <div
      className="rounded-xl p-3 text-center mb-3"
      style={{ backgroundColor: 'rgba(30, 27, 75, 0.03)' }}
    >
      <span
        className="inline-block px-2 py-0.5 text-white text-xs font-semibold rounded-full mb-1"
        style={{ backgroundColor: v5.colors.amber, fontFamily: v5.fonts.body }}
      >
        Expired
      </span>
      <p
        className="line-through text-sm"
        style={{ fontFamily: v5.fonts.mono, color: v5.colors.textHint }}
      >
        {claim.code}
      </p>
    </div>

    <Link
      to={`/business/${claim.business_slug}`}
      className="block text-center py-2.5 rounded-xl text-white text-sm font-semibold transition-all hover:-translate-y-0.5"
      style={{
        background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
        fontFamily: v5.fonts.body,
        boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
      }}
    >
      <RefreshCw className="w-4 h-4 inline mr-2" />
      Claim Again
    </Link>
  </div>
);

const RedeemedClaimsSection = ({ claims, getBusinessEmoji, formatDate }) => (
  <section className="mb-16">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
        >
          <Check className="w-6 h-6" style={{ color: v5.colors.green }} />
        </div>
        <div>
          <p
            style={{
              fontFamily: v5.fonts.mono,
              fontSize: '12px',
              color: v5.colors.textHint
            }}
          >
            // redeemed_offers
          </p>
          <h2
            className="text-2xl"
            style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
          >
            Redeemed Offers
          </h2>
        </div>
      </div>
      <span
        className="px-3 py-1 rounded-full text-sm font-semibold"
        style={{
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          color: v5.colors.green,
          fontFamily: v5.fonts.body
        }}
      >
        {claims.length} Used
      </span>
    </div>

    <div className="grid md:grid-cols-3 gap-4">
      <AnimatePresence>
        {claims.map((claim, index) => (
          <motion.div
            key={claim.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: index * 0.05 }}
          >
            <RedeemedClaimCard
              claim={claim}
              emoji={getBusinessEmoji(claim.business_name)}
              formatDate={formatDate}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  </section>
);

const RedeemedClaimCard = ({ claim, emoji, formatDate }) => (
  <div
    className="rounded-xl p-5 transition-all hover:-translate-y-1"
    style={{
      backgroundColor: v5.colors.white,
      border: `1px solid ${v5.colors.borderLight}`,
      boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
      borderLeft: `4px solid ${v5.colors.green}`
    }}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)' }}
        >
          <span className="text-xl grayscale opacity-60">{emoji}</span>
        </div>
        <div>
          <h4
            className="font-semibold"
            style={{ fontFamily: v5.fonts.body, color: v5.colors.textPrimary }}
          >
            {claim.business_name}
          </h4>
          <p
            className="text-xs"
            style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
          >
            Claimed {formatDate(claim.claimed_at)}
          </p>
        </div>
      </div>
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
      >
        <Check className="w-4 h-4" style={{ color: v5.colors.green }} />
      </div>
    </div>

    <div className="mb-3">
      <p
        className="text-sm line-clamp-2"
        style={{ fontFamily: v5.fonts.body, color: v5.colors.textSecondary }}
      >
        {claim.deal_title}
      </p>
    </div>

    <div
      className="rounded-xl p-3 text-center mb-3"
      style={{ backgroundColor: 'rgba(30, 27, 75, 0.03)' }}
    >
      <span
        className="inline-block px-2 py-0.5 text-white text-xs font-semibold rounded-full mb-1"
        style={{ backgroundColor: v5.colors.green, fontFamily: v5.fonts.body }}
      >
        Redeemed
      </span>
      <p
        className="line-through"
        style={{ fontFamily: v5.fonts.mono, color: v5.colors.textHint }}
      >
        {claim.code}
      </p>
      <p
        className="text-xs"
        style={{ fontFamily: v5.fonts.body, color: v5.colors.textHint }}
      >
        {formatDate(claim.redeemed_at || claim.claimed_at)}
      </p>
    </div>

    <Link
      to={`/business/${claim.business_slug}`}
      className="block text-center py-2 text-sm font-medium transition-colors"
      style={{ color: v5.colors.textMuted, fontFamily: v5.fonts.body }}
    >
      View Business →
    </Link>
  </div>
);

const CTASection = () => (
  <section
    className="py-16"
    style={{ backgroundColor: v5.colors.navy }}
  >
    <div className="max-w-4xl mx-auto px-6 text-center">
      <p
        className="mb-2"
        style={{
          fontFamily: v5.fonts.mono,
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.4)'
        }}
      >
        // for_businesses
      </p>
      <h2
        className="text-3xl md:text-4xl mb-4"
        style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.white }}
      >
        Own a Local Small Business?
      </h2>
      <p
        className="mb-8 max-w-lg mx-auto"
        style={{ fontFamily: v5.fonts.body, color: 'rgba(255, 255, 255, 0.6)' }}
      >
        Join Tappd and turn nearby bar traffic into your customers.
      </p>
      <Link
        to="/for-businesses"
        className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-white font-bold transition-all hover:-translate-y-0.5"
        style={{
          background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.orangeDark} 100%)`,
          fontFamily: v5.fonts.body,
          boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4), 0 0 30px rgba(249, 115, 22, 0.2)'
        }}
      >
        List Your Business on Tappd
        <ChevronRight className="w-5 h-5" />
      </Link>
    </div>
  </section>
);

export default MyCodes;
